/**
 * MSAL configuration — master spec §8d, Phase 1 step 7.
 *
 * The four client ids and both Application ID URIs arrived on 2026-09-08 and
 * are pinned in `infra/config/{dev,prod}.ts`. §0 still forbids a placeholder
 * here: a wrong client id produces a token that validates against nothing and a
 * 401 carrying no useful message.
 *
 * **Vite inlines these at BUILD time.** The deployed bundle is compiled inside
 * a container where the repo's `.env` does not exist, so the values reach it
 * only as Docker build args passed by CDK from `infra/config/`. When that
 * wiring was missing the SPA compiled with none of them — and, under the old
 * rule below, quietly offered the dev-login bypass instead.
 *
 * **The bypass is now opt-in rather than a fallback** (Phase 6 [R6],
 * "Three contracts that only a real deploy tests"). `VITE_DEV_LOGIN=1` selects
 * it; nothing else does. A build without it and without Entra is
 * `misconfigured`, which fails loudly and names what is absent — because
 * turning a missing value into a working-looking screen is exactly what §0's
 * placeholder rule exists to prevent.
 */

import {
  type Configuration,
  LogLevel,
  PublicClientApplication,
  type RedirectRequest,
} from '@azure/msal-browser';

/**
 * `misconfigured` is not a mode the app runs in — it is the mode it refuses in.
 * It exists so the screen can say which variables are missing instead of
 * silently degrading.
 */
export type AuthMode = 'entra' | 'dev' | 'misconfigured';

const tenantId = import.meta.env.VITE_ENTRA_TENANT_ID?.trim();
const spaClientId = import.meta.env.VITE_ENTRA_SPA_CLIENT_ID?.trim();
const apiScope = import.meta.env.VITE_ENTRA_API_SCOPE?.trim();

/**
 * A compile-time literal (`vite.config.ts`), not an env read.
 *
 * That is what lets Rollup remove the bypass rather than merely leave it
 * unreachable — `import.meta.env.VITE_DEV_LOGIN` at module scope kept
 * `x-dev-user`, the dev sign-in screen and the user switcher in the bundle.
 */
export const devLoginEnabled = __DEV_LOGIN__;

/**
 * Entra is usable only when all three are present. A partial configuration is
 * the dangerous state — it looks configured and fails at the token endpoint —
 * so it is treated as unconfigured and reported.
 */
export const entraConfigured = Boolean(tenantId && spaClientId && apiScope);

/**
 * Entra wins wherever it is configured; `dev` requires `VITE_DEV_LOGIN=1`
 * EXPLICITLY; anything else is a refusal.
 *
 * The old rule was `entraConfigured ? 'entra' : 'dev'` — a fallback. It is
 * right locally, where an unconfigured tenant means "use the bypass", and wrong
 * everywhere else, where it means "this build is broken". The deployed SPA took
 * the second branch and offered a bypass the API correctly rejects, so every
 * sign-in failed against a screen that looked deliberate.
 *
 * The bypass compiles out of any build that does not set `VITE_DEV_LOGIN=1`,
 * which the Docker build does not.
 */
export function resolveAuthMode(entra: boolean, devLogin: boolean): AuthMode {
  if (entra) return 'entra';
  return devLogin ? 'dev' : 'misconfigured';
}

/**
 * A pure function above, and its application here.
 *
 * The split is what makes this testable: `__DEV_LOGIN__` is a compile-time
 * literal, so a test cannot vary it through the environment — and the whole
 * point of the literal is that it folds. `resolveAuthMode` takes both booleans
 * as arguments, so every combination is exercised directly, while the call site
 * keeps the literal and Rollup keeps folding.
 */
export const authMode: AuthMode = resolveAuthMode(entraConfigured, devLoginEnabled);

/** What is missing, for the sign-in screen to say so plainly rather than 401ing. */
export function missingEntraConfig(): string[] {
  const missing: string[] = [];
  if (!tenantId) missing.push('VITE_ENTRA_TENANT_ID');
  if (!spaClientId) missing.push('VITE_ENTRA_SPA_CLIENT_ID');
  if (!apiScope) missing.push('VITE_ENTRA_API_SCOPE');
  return missing;
}

/** The scopes every token request asks for. */
export const apiScopes: string[] = apiScope ? [apiScope] : [];

export function buildMsalConfig(): Configuration {
  return {
    auth: {
      clientId: spaClientId!,
      authority: `https://login.microsoftonline.com/${tenantId}`,
      // Entra matches redirect URIs EXACTLY, including the trailing slash.
      // `http://localhost:5173/` is confirmed on the dev SPA registration.
      redirectUri: `${window.location.origin}/`,
      postLogoutRedirectUri: `${window.location.origin}/logout`,
      // We restore the intended path ourselves from MSAL `state`, which
      // survives the round trip more reliably than MSAL's own navigation and
      // lets us keep the query string and hash. See `auth/deep-link.ts`.
      navigateToLoginRequestUrl: false,
    },
    cache: {
      /*
       * sessionStorage, and it is worth being honest about why.
       *
       * It is readable by any script on the origin EXACTLY as localStorage is.
       * The only difference is lifetime. The control that actually protects
       * the token is the CSP's `script-src 'self'` with no `'unsafe-inline'`
       * (Phase 6) — so treat a CSP relaxation as a token-exfiltration risk,
       * not a styling decision.
       */
      cacheLocation: 'sessionStorage',
      storeAuthStateInCookie: false,
    },
    system: {
      // MSAL falls back to a hidden iframe for silent renewal; point it at the
      // deliberately blank page rather than booting a second copy of the SPA.
      redirectNavigationTimeout: 30_000,
      loggerOptions: {
        logLevel: LogLevel.Warning,
        piiLoggingEnabled: false,
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) return;
          if (level === LogLevel.Error) console.error('[msal]', message);
          else if (level === LogLevel.Warning) console.warn('[msal]', message);
        },
      },
    },
  };
}

export function createMsalInstance(): PublicClientApplication {
  return new PublicClientApplication(buildMsalConfig());
}

/**
 * Redirect, NOT popup. Popups break under corporate browser policy and under
 * Conditional Access, both of which apply here (§8c).
 */
export function loginRequest(state: string): RedirectRequest {
  return { scopes: apiScopes, state, redirectStartPage: `${window.location.origin}/` };
}

/** MSAL's silent-renewal iframe target. Registered as a redirect URI on both SPAs. */
export const AUTH_REDIRECT_IFRAME_PATH = '/auth-redirect.html';
